import axios from 'axios'
import Logger from '@/services/Logger'

// import router from '@/router/index'

/**
 * The base service for backend API calls.
 * @param {string} baseUrl
 */
class ApiServiceBase {
  /**
   * The base service for backend API calls.
   * @param {Object} options
   * @param {string} options.baseUrl Base URL for making API calls
   */
  constructor ({ baseUrl }) {
    this.http = axios.create({
      baseURL: baseUrl,
      withCredentials: false,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })

    this.http.interceptors.response.use(
      response => {
        return response
      },
      error => {
        if (!error.response) {
          // router.push({
          //   name: 'Error',
          //   params: {
          //     errorCode: 'api'
          //   }
          // })

          // error.handled = true
        } else {
          switch (error.response.status) {
            case 500:
            case 400:
              if (error.response.data.errors) {
                if (Array.isArray(error.response.data.errors)) {
                  const errors = []
                  error.response.data.errors.forEach(e => {
                    errors.push(e.description)
                  })

                  Logger.error(errors)
                  return Promise.reject({ message: error.response.data.message, errors: errors})
                } else if (Array.isArray(error.response.data.errors.$)) {
                  const errors = error.response.data.errors.$;
                  return Promise.reject({message: errors[0], errors: errors})
                } else {
                  return Promise.reject({ message: error.response.data.message, errors: [error.response.data.errors]})
                }
              } else {
                Logger.error(error.response.data)
                return Promise.reject(error.response.data)
              }
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Adds the Bearer token authorization header to future requests.
   * @param {string} token The authorization token
   */
  setAuthorizationHeader (token) {
    this.http.defaults.headers.common.Authorization = `Bearer ${token}`
  }

  /**
   * Remove the authorization token header from any future requests.
   */
  clearAuthorizationHeader () {
    this.http.defaults.headers.common.Authorization = ''
  }

  // REST Methods
  /**
   * Enumerate resources with a GET request to BASE_URL/resource?query
   * @param {string} resource Name of the resource type
   * @param {Object} query Search parameters for the request
   * @returns {Promise}
   */
  find ({ resource, query }) {
    return this.http.get(resource, {
      params: query,
      data: {}
    })
  }

  /**
   * Retrieve a resource by ID with a GET request to BASE_URL/resource/id?query
   * @param {string} resource Name of the resource type
   * @param {string} id ID of the resource to retrieve
   * @param {Object} query Additional parameters to attach to the request
   * @returns {Promise}
   */
  get ({ resource, id, query }) {
    return this.http.get(`${resource}/${id}`, {
      params: query
    })
  }

  /**
   * Submits a resource with a POST request to BASE_URL/resource?query
   * @param {string} resource Name of the resource type
   * @param {Object} data Data to be posted
   * @param {Object} query Additional parameters to attach to the request
   * @returns {Promise}
   */
  post ({ resource, data, query }) {
    return this.http.post(resource, data, {
      params: query
    })
  }

  /**
   * Create a resource with a POST request to BASE_URL/resource?query
   * @param {string} resource Name of the resource type
   * @param {Object} data Data to be posted
   * @param {Object} query Additional parameters to attach to the request
   * @returns {Promise}
   */
  create ({ resource, data, query }) {
    return this.post({ resource, data, query })
  }

  /**
   * Update a resource by ID with a PATCH request to BASE_URL/resource/id?query
   * @param {string} resource Name of the resource type
   * @param {string} id ID of the resource to update
   * @param {Object} data Data to be posted
   * @param {Object} query Additional parameters to attach to the request
   * @returns {Promise}
   */
  update ({ resource, id, data, query }) {
    return this.http.patch(`${resource}/${id}`, data, {
      params: query,
      headers: {
        'Content-Type': 'application/json-patch+json'
      }
    })
  }

  /**
   * Delete a resource by ID with a DELETE request to BASE_URL/resource/id
   * @param {string} resource
   * @param {string} id
   * @returns {Promise}
   */
  delete ({ resource, id }) {
    return this.http.delete(`${resource}/${id}`)
  }

  /**
   * @callback CustomApiAction
   * @param {AxiosInstance} http
   * @returns {Promise}
   */
  /**
   * 
   * @param {CustomApiAction} action 
   */
  custom (action) {
    return action(this.http)
  }
}

export default new ApiServiceBase({
  baseUrl: process.env.VUE_APP_API_BASE_URL
})
